import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["new", "contacted", "consultation_scheduled", "qualified", "proposal_sent", "won", "lost", "archived"];

export function AdminEnquiries() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [projectType, setProjectType] = useState("");
  const [spam, setSpam] = useState(false);

  const load = () => {
    const p = new URLSearchParams();
    if (status) p.set("status", status);
    if (q) p.set("q", q);
    if (projectType) p.set("project_type", projectType);
    if (spam) p.set("spam", "true");
    api.get(`/admin/enquiries?${p.toString()}`).then((r) => setItems(r.data));
  };
  useEffect(load, [status, q, projectType, spam]);

  const exportCsv = () => {
    window.open(`${BACKEND_URL}/api/admin/enquiries-export/csv`, "_blank");
  };

  return (
    <div data-testid="page-admin-enquiries" className="max-w-7xl">
      <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#7A736C] mb-2">CRM</div>
          <h1 className="font-serif text-4xl tracking-tight">Enquiries</h1>
        </div>
        <button onClick={exportCsv} data-testid="enquiries-export" className="px-5 py-2.5 bg-[#2B2927] text-white text-xs uppercase tracking-[0.2em] rounded-sm hover:bg-[#A85C42] transition-colors">Export CSV</button>
      </div>
      <div className="flex flex-wrap gap-3 mb-6">
        <input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="enquiries-search" className="bg-white border border-[#D5D0C8] px-4 py-2 rounded-sm text-sm" />
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="enquiries-status" className="bg-white border border-[#D5D0C8] px-4 py-2 rounded-sm text-sm">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
        <select value={projectType} onChange={(e) => setProjectType(e.target.value)} data-testid="enquiries-type" className="bg-white border border-[#D5D0C8] px-4 py-2 rounded-sm text-sm">
          <option value="">All types</option><option value="residential">Residential</option><option value="commercial">Commercial</option>
        </select>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={spam} onChange={(e) => setSpam(e.target.checked)} /> Show spam</label>
      </div>
      <div className="bg-white border border-[#D5D0C8] rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#EAE6DF] text-[#7A736C] text-xs uppercase tracking-[0.2em]">
            <tr><th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Contact</th><th className="text-left px-4 py-3">Project</th><th className="text-left px-4 py-3">Status</th><th className="text-left px-4 py-3">Date</th></tr>
          </thead>
          <tbody>
            {items.map((e) => (
              <tr key={e.id} data-testid={`enquiry-row-${e.id}`} className="border-t border-[#EAE6DF] hover:bg-[#F9F8F6]">
                <td className="px-4 py-3"><Link to={`/admin/enquiries/${e.id}`} className="font-medium hover:text-[#A85C42]">{e.name}</Link></td>
                <td className="px-4 py-3 text-[#4A4642]">{e.email}<br /><span className="text-xs text-[#7A736C]">{e.phone || ""}</span></td>
                <td className="px-4 py-3 text-[#4A4642]">{e.project_location || "—"}<br /><span className="text-xs text-[#7A736C]">{e.project_type} · {(e.services || []).slice(0, 2).join(", ")}</span></td>
                <td className="px-4 py-3"><span className="text-xs uppercase tracking-[0.2em] text-[#A85C42]">{e.status}</span></td>
                <td className="px-4 py-3 text-xs text-[#7A736C]">{new Date(e.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-[#7A736C]">No enquiries.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminEnquiryDetail() {
  const { id } = useParams();
  const [e, setE] = useState(null);
  const [note, setNote] = useState("");
  const [status, setStatus] = useState("");
  const [assigned, setAssigned] = useState("");
  const [followUp, setFollowUp] = useState("");

  const load = () => api.get(`/admin/enquiries/${id}`).then((r) => { setE(r.data); setStatus(r.data.status); setAssigned(r.data.assigned_to || ""); setFollowUp(r.data.follow_up_date || ""); });
  useEffect(() => { load(); }, [id]);

  const save = async (patch) => {
    try { await api.put(`/admin/enquiries/${id}`, patch); toast.success("Saved"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  if (!e) return <div>Loading…</div>;

  return (
    <div data-testid="page-enquiry-detail" className="max-w-5xl">
      <Link to="/admin/enquiries" className="text-xs uppercase tracking-[0.2em] text-[#7A736C] hover:text-[#A85C42]">← Enquiries</Link>
      <h1 className="font-serif text-4xl tracking-tight mt-4">{e.name}</h1>
      <div className="text-[#7A736C] text-sm mt-1">{e.email} · {e.phone || "—"}</div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-[#D5D0C8] p-6 rounded-sm">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#7A736C] mb-3">Project details</div>
            <dl className="grid grid-cols-2 gap-4 text-sm">
              <div><dt className="text-xs text-[#7A736C]">Location</dt><dd>{e.project_location || "—"}</dd></div>
              <div><dt className="text-xs text-[#7A736C]">Property type</dt><dd>{e.property_type || "—"}</dd></div>
              <div><dt className="text-xs text-[#7A736C]">Project type</dt><dd>{e.project_type || "—"} / {e.work_type || "—"}</dd></div>
              <div><dt className="text-xs text-[#7A736C]">Size</dt><dd>{e.property_size || "—"}</dd></div>
              <div><dt className="text-xs text-[#7A736C]">Budget</dt><dd>{e.budget_range || "—"}</dd></div>
              <div><dt className="text-xs text-[#7A736C]">Start date</dt><dd>{e.start_date || "—"}</dd></div>
              <div className="col-span-2"><dt className="text-xs text-[#7A736C]">Services</dt><dd>{(e.services || []).join(", ") || "—"}</dd></div>
              <div className="col-span-2"><dt className="text-xs text-[#7A736C]">Preferred contact</dt><dd>{e.preferred_contact || "—"}</dd></div>
              <div className="col-span-2"><dt className="text-xs text-[#7A736C]">Message</dt><dd className="whitespace-pre-wrap">{e.description || "—"}</dd></div>
            </dl>
          </div>
          <div className="bg-white border border-[#D5D0C8] p-6 rounded-sm">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#7A736C] mb-3">Notes & activity</div>
            <ul className="space-y-3 mb-4">
              {(e.notes || []).map((n, i) => (
                <li key={i} className="bg-[#F2EFE9] p-3 rounded-sm text-sm"><div className="text-[#4A4642] whitespace-pre-wrap">{n.text}</div><div className="text-xs text-[#7A736C] mt-1">{n.by} · {new Date(n.at).toLocaleString()}</div></li>
              ))}
            </ul>
            <textarea rows={3} placeholder="Add an internal note…" value={note} onChange={(e) => setNote(e.target.value)} data-testid="enquiry-note" className="w-full bg-white border border-[#D5D0C8] p-3 rounded-sm text-sm outline-none focus:border-[#A85C42]" />
            <button onClick={() => { if (note) { save({ note }); setNote(""); } }} data-testid="enquiry-add-note" className="mt-2 px-5 py-2 bg-[#2B2927] text-white text-xs uppercase tracking-[0.2em] rounded-sm">Add note</button>
            <div className="mt-6 text-xs text-[#7A736C]">Activity log</div>
            <ul className="mt-2 text-xs text-[#7A736C] space-y-1">
              {(e.activities || []).slice().reverse().map((a, i) => (
                <li key={i}>· {a.type.replace(/_/g, " ")} {a.value ? `→ ${a.value}` : ""} {a.by ? `by ${a.by}` : ""} · {new Date(a.at).toLocaleString()}</li>
              ))}
            </ul>
          </div>
        </div>
        <div className="space-y-6">
          <div className="bg-white border border-[#D5D0C8] p-6 rounded-sm">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#7A736C] mb-4">Manage</div>
            <label className="block mb-3"><span className="block text-xs text-[#7A736C] mb-1">Status</span>
              <select value={status} onChange={(e) => { setStatus(e.target.value); save({ status: e.target.value }); }} data-testid="enquiry-status" className="w-full bg-white border border-[#D5D0C8] p-2 rounded-sm text-sm">
                {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
              </select>
            </label>
            <label className="block mb-3"><span className="block text-xs text-[#7A736C] mb-1">Assigned to (email)</span>
              <input value={assigned} onChange={(e) => setAssigned(e.target.value)} onBlur={() => save({ assigned_to: assigned })} data-testid="enquiry-assigned" className="w-full bg-white border border-[#D5D0C8] p-2 rounded-sm text-sm" />
            </label>
            <label className="block mb-3"><span className="block text-xs text-[#7A736C] mb-1">Follow-up date</span>
              <input type="date" value={followUp} onChange={(e) => { setFollowUp(e.target.value); save({ follow_up_date: e.target.value }); }} data-testid="enquiry-followup" className="w-full bg-white border border-[#D5D0C8] p-2 rounded-sm text-sm" />
            </label>
            <button onClick={() => save({ spam: !e.spam })} data-testid="enquiry-spam" className="w-full mt-2 py-2 border border-[#D5D0C8] rounded-sm text-xs uppercase tracking-[0.2em] hover:bg-[#F2EFE9]">{e.spam ? "Unmark" : "Mark"} as spam</button>
          </div>
          <div className="bg-white border border-[#D5D0C8] p-6 rounded-sm text-xs text-[#7A736C]">
            <div className="text-[10px] uppercase tracking-[0.28em] mb-3">Source</div>
            <div>· Channel: {e.source || "website"}</div>
            {e.utm && Object.entries(e.utm).map(([k, v]) => <div key={k}>· {k}: {v}</div>)}
            <div>· IP: {e.ip || "—"}</div>
            <div>· Received: {new Date(e.created_at).toLocaleString()}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
